import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, TextFieldProps } from "@mui/material";
import { getRequest } from "../../../api/apiServices";
import apiList from "../../../api/apiList";
import { VonqOption, VonqTaxonomyLink } from "../../../types/vonqMapping";
import { linkToOption, optionToLink } from "./vonqFieldUtils";

export type VonqTaxonomyKind =
  | "education"
  | "seniority"
  | "jobFunction"
  | "industry";

const TAXONOMY_ENDPOINTS: Record<VonqTaxonomyKind, string> = {
  education: apiList.getEducation,
  seniority: apiList.getSeniority,
  jobFunction: apiList.getJobFunction,
  industry: apiList.getIndustries,
};

export interface VonqTaxonomySelectProps {
  kind: VonqTaxonomyKind;
  vonqLink?: VonqTaxonomyLink | null;
  onVonqChange: (link: VonqTaxonomyLink | undefined) => void;
  /** Selecting an option also updates a linked display text field when provided */
  onDisplayChange?: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  textFieldProps?: Partial<TextFieldProps>;
}

const VonqTaxonomySelect: React.FC<VonqTaxonomySelectProps> = ({
  kind,
  vonqLink,
  onVonqChange,
  onDisplayChange,
  placeholder,
  error,
  helperText,
  textFieldProps,
}) => {
  const [options, setOptions] = useState<VonqOption[]>([]);
  const [selected, setSelected] = useState<VonqOption | null>(linkToOption(vonqLink));
  const [loaded, setLoaded] = useState(false);

  const defaultPlaceholder =
    placeholder ||
    {
      education: "Select education level",
      seniority: "Select seniority",
      jobFunction: "Select job function",
      industry: "Select industry",
    }[kind];

  useEffect(() => {
    setSelected(linkToOption(vonqLink));
  }, [vonqLink?.vonqId, vonqLink?.description]);

  useEffect(() => {
    if (loaded) return;
    getRequest(TAXONOMY_ENDPOINTS[kind])
      .then((response) => {
        setOptions(response?.data?.data || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [kind, loaded]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      onChange={(_, option) => {
        setSelected(option);
        const link = optionToLink(option);
        onVonqChange(link);
        if (onDisplayChange) {
          onDisplayChange(option?.name || "");
        }
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onOpen={() => {
        if (!loaded) {
          getRequest(TAXONOMY_ENDPOINTS[kind])
            .then((response) => {
              setOptions(response?.data?.data || []);
              setLoaded(true);
            })
            .catch(() => setLoaded(true));
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          placeholder={defaultPlaceholder}
          error={error}
          helperText={helperText}
          className={textFieldProps?.className || "input-box-custom"}
        />
      )}
    />
  );
};

export default VonqTaxonomySelect;
